import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Flex, Form, Modal, Spin, Tabs } from "antd";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";
import { useMessageApi } from "../../context/MessageContext";
import { MasterDataServices } from "../../services/master-data";
import { UserServices } from "../../services/user";
import { SupplierServices } from "../../services/supplier";
import GeneralInfoForm from "./components/general-info-form";
import PaymentTermsForm from "./components/payment-terms-form";
import "./create-and-edit.supplier.scss";
import type { IMasterDataSupplier } from "../../interfaces/master-data";
import MappingsForm from "./components/mappings-form";
import {
  IRequestSupplierGeneralInfo,
  ISupplierCreateAndDetailResponse,
  ISupplierRequest,
} from "../../interfaces/supplier";
import { useAuth } from "../../context/AuthContext";

const DefaultTabKey = "info";

const CreateAndEditSupplierPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { user } = useAuth();
  const { roles = [] } = user || {};
  const isAdmin = roles[0]?.name === "Admin";

  const hasSupplierEditor = useHasPermission([PERMISSIONS.SUPPLIER_EDITOR]);
  const messageApi = useMessageApi();

  const [loading, setLoading] = useState(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(DefaultTabKey);
  const [isModalCurrencySync, setIsModalCurrencySync] = useState(false);

  const [masterData, setMasterData] = useState<IMasterDataSupplier>({
    countries: [],
    paymentMethods: [],
    currencies: [],
    termTypes: [],
    installments: [],
    eventTypes: [],
    timings: [],
    absoluteDrivers: [],
    calculationBasis: [],
    accountDebits: [],
    accountCredits: [],
    autosMatch: [],
    users: [],
    productHead: [],
    productOwner: [],
    apOwner: [],
    apEscalation: [],
    defaultTermsRule: {},
  });

  const [initialData, setInitialData] = useState<any>({});
  const [generalForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [mappingForm] = Form.useForm();

  const fetchMasterData = async () => {
    try {
      const types = [
        "COUNTRY",
        "PAYMENT_METHOD",
        "CURRENCY",
        "TERM_TYPE",
        "NUM_OF_INSTALLMENT",
        "EVENT_TYPE",
        "TIMING",
        "ABSOLUTE_DRIVER",
        "CALCULATION_BASIS",
        "PAYMENT_TERM_RULE_NOMINAL_ACCOUNT_DEBIT",
        "PAYMENT_TERM_RULE_NOMINAL_ACCOUNT_CREDIT",
        "PAYMENT_TERM_RULE_UNDERS_OVERS_AUTO_MATCH",
      ];

      const masterPromises = types.map((type) => MasterDataServices.getAllByType(type).then((res) => res?.data || []));

      const [usersByRole, users, defaultTermsRule] = await Promise.all([
        SupplierServices.getListUserBaseRole().then((res) => res?.data || {}),
        UserServices.getBasic().then((res) => res?.data || {}),
        SupplierServices.getDefaultTermsRule().then((res) => res?.data || {}),
      ]);

      const [
        countries,
        paymentMethods,
        currencies,
        termTypes,
        installments,
        eventTypes,
        timings,
        absoluteDrivers,
        calculationBasis,
        accountDebits,
        accountCredits,
        autosMatch,
      ] = await Promise.all(masterPromises);

      setMasterData({
        countries,
        paymentMethods,
        currencies,
        termTypes,
        installments,
        eventTypes,
        timings,
        absoluteDrivers,
        calculationBasis,
        accountDebits,
        accountCredits,
        autosMatch,
        users,
        productHead: usersByRole?.productHead || [],
        productOwner: usersByRole?.productOwner || [],
        apOwner: usersByRole?.apOwner || [],
        apEscalation: usersByRole?.apEscalation || [],
        defaultTermsRule,
      });

      if (!isEditMode && defaultTermsRule) {
        setInitialData({
          paymentTermsRules: [
            {
              ...defaultTermsRule,
              rule: {
                ...defaultTermsRule.rule,
                isApplyAllDefaultSettings: true,
                isApplyDefaultTolerance: true,
                isApplyDefaultSageCodes: true,
              },
            },
          ],
        });
      }
    } catch (error) {
      messageApi.error("Failed to load master data.");
    }
  };

  const fetchSupplierDetail = async () => {
    if (!isEditMode) return;
    try {
      const response: any = await SupplierServices.getSupplierDetail(id!);
      const data = response?.data;
      if (data) {
        setInitialData(data);
      }
    } catch (error) {
      messageApi.error("Failed to load supplier detail.");
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await fetchMasterData();
        if (isEditMode) {
          await fetchSupplierDetail();
        }
      } catch (error) {
        messageApi.error("Failed to initialize page.");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onChangeTab = async (newKey: string) => {
    if (hasSupplierEditor) {
      try {
        if (activeTabKey === "info") {
          await generalForm.validateFields();
        } else if (activeTabKey === "payment") {
          await paymentForm.validateFields();
        }
        setActiveTabKey(newKey);
      } catch {
        messageApi.error("Please fill in all information before switching tabs.");
      }
    } else setActiveTabKey(newKey);
  };

  const handleSubmitAll = async (isSyncAllTransaction = false) => {
    try {
      const generalValues: IRequestSupplierGeneralInfo = await generalForm.validateFields();
      const paymentValues = await paymentForm.validateFields();
      const mapping = await mappingForm.validateFields();

      const payload: ISupplierRequest = {
        generalInfo: generalValues,
        ...paymentValues,
        ...mapping,
        syncAllTransaction: isSyncAllTransaction,
      };

      if (
        isEditMode &&
        isAdmin &&
        !isModalCurrencySync &&
        generalValues.currency !== initialData?.generalInfo?.currency
      ) {
        setIsModalCurrencySync(true);
        return;
      }

      setIsLoadingSubmit(true);
      let response: ISupplierCreateAndDetailResponse;

      if (isEditMode) {
        response = await SupplierServices.updateSupplier(id!, payload);
      } else {
        response = await SupplierServices.createSupplier(payload);
      }

      if (response.success) {
        messageApi.success(response.message);
        setIsModalCurrencySync(false);
        navigate("/supplier");
      }
    } catch (err: any) {
      messageApi.error(err?.message || "Submission failed.");
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const tabItems = [
    {
      label: "General Info",
      key: "info",
      children: <GeneralInfoForm form={generalForm} initialValues={initialData.generalInfo} masterData={masterData} />,
      forceRender: true,
    },
    {
      label: "Payment Terms & Rules",
      key: "payment",
      children: (
        <PaymentTermsForm form={paymentForm} initialValues={initialData.paymentTermsRules} masterData={masterData} />
      ),
      forceRender: true,
    },
    {
      label: "Mappings",
      key: "mapping",
      children: <MappingsForm form={mappingForm} initialValues={{ mapping: initialData.mapping }} />,
      forceRender: true,
    },
  ];

  const renderHeader = () => (
    <Flex justify="space-between" align="center" gap={16}>
      <div className="title">
        <h2 className="title-page">
          {isEditMode ? `${hasSupplierEditor ? "Edit" : "View"} Supplier` : "Add New Supplier"}
        </h2>
        <h4 style={{ margin: 0, marginBottom: 5 }}>
          {isEditMode ? `${initialData?.generalInfo?.supplierCode} - ${initialData?.generalInfo?.supplierName}` : null}
        </h4>
      </div>
      <Flex gap={16} justify="end">
        <Link to="/supplier">
          <Button disabled={isLoadingSubmit}>Cancel</Button>
        </Link>
        {hasSupplierEditor && (
          <Button type="primary" onClick={() => handleSubmitAll()} loading={isLoadingSubmit}>
            Save
          </Button>
        )}
      </Flex>
    </Flex>
  );

  const renderModalSyncTransactions = () => (
    <Modal
      open={isModalCurrencySync}
      onCancel={() => setIsModalCurrencySync(false)}
      footer={null}
      title={"Confirm Sync all Transactions"}
      width={600}
      closable={false}
    >
      <div style={{ marginBottom: "20px", textAlign: "center", fontSize: "18px" }}>
        Changing the currency will affect all transactions for this supplier (including old transactions). Do you wish
        to proceed?
      </div>
      <Flex gap={16} justify="end">
        <Button onClick={() => setIsModalCurrencySync(false)} disabled={isLoadingSubmit}>
          Cancel
        </Button>
        <Button color="primary" variant="outlined" onClick={() => handleSubmitAll()} loading={isLoadingSubmit}>
          Save without Sync
        </Button>
        <Button type="primary" onClick={() => handleSubmitAll(true)} loading={isLoadingSubmit}>
          Sync all transactions
        </Button>
      </Flex>
    </Modal>
  );

  if (loading) {
    return (
      <Flex gap="middle" vertical style={{ padding: "2rem" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div className={`detail-supplier-page ${hasSupplierEditor ? "" : "fm-view-page"}`}>
      {renderHeader()}
      <Tabs activeKey={activeTabKey} onChange={onChangeTab} items={tabItems} />
      {renderModalSyncTransactions()}
    </div>
  );
};

export default CreateAndEditSupplierPage;
